import "@/App.css";
import "@fontsource/unbounded/700.css";
import "@fontsource/unbounded/900.css";
import "@fontsource/manrope/400.css";
import "@fontsource/manrope/500.css";
import "@fontsource/manrope/600.css";
import "@fontsource/jetbrains-mono/400.css";

import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { useEffect } from "react";
import { Toaster } from "@/components/ui/sonner";
import Layout from "@/components/Layout";
import Dashboard from "@/pages/Dashboard";
import Games from "@/pages/Games";
import GameDetail from "@/pages/GameDetail";
import AchievementsBrowse from "@/pages/AchievementsBrowse";
import Statistics from "@/pages/Statistics";
import Settings from "@/pages/Settings";
import Tutorials from "@/pages/Tutorials";
import TutorialDetail from "@/pages/TutorialDetail";
import TutorialsAdmin from "@/pages/TutorialsAdmin";
import Browse from "@/pages/Browse";
import BrowseGameInfo from "@/pages/BrowseGameInfo";
import VersionHub from "@/pages/VersionHub";
import WishList from "@/pages/WishList";
import Social from "@/pages/Social";
import ImportExport from "@/pages/ImportExport";
import LibraryMaintenance from "@/pages/LibraryMaintenance";
import Priorities from "@/pages/Priorities";
import MasterDatabase from "@/pages/MasterDatabase";
import MasterDatabaseConfig from "@/pages/MasterDatabaseConfig";
import MasterDatabaseCommunity from "@/pages/MasterDatabaseCommunity";
import Notifications from "@/pages/Notifications";
import Login from "@/pages/Login";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import VerifyEmail from "@/pages/VerifyEmail";
import Unsubscribe from "@/pages/Unsubscribe";
import SignatureGenerator from "@/pages/SignatureGenerator";
import SuperAdminDashboard from "@/pages/SuperAdminDashboard";
import SuperAdminModeration from "@/pages/SuperAdminModeration";
import Links from "@/pages/Links";
import MasterDatabaseLinks from "@/pages/MasterDatabaseLinks";
import MasterDatabaseTags from "@/pages/MasterDatabaseTags";
import MasterDatabaseMilestones from "@/pages/MasterDatabaseMilestones";
import MasterDatabaseImages from "@/pages/MasterDatabaseImages";
import Upgrade from "@/pages/Upgrade";
import Multiplayer from "@/pages/Multiplayer";
import AccountSettings from "@/pages/AccountSettings";
import LandingPage from "@/pages/LandingPage";
import FeaturesPage from "@/pages/FeaturesPage";
import ProtectedRoute, { AuthProvider } from "@/components/ProtectedRoute";
import AdminRoute from "@/components/AdminRoute";

// Scroll to top on every route change
function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

function AppRouter() {
  return (
    <AuthProvider>
      <ScrollToTop />
      <Routes>
        {/* Public routes - No authentication required */}
        <Route path="/" element={<LandingPage />} />
        <Route path="/features" element={<FeaturesPage />} />
        <Route path="/login" element={<Login />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/verify-email" element={<VerifyEmail />} />
        <Route path="/unsubscribe" element={<Unsubscribe />} />
        
        {/* Protected routes - require authentication */}
        <Route
          path="/app"
          element={
            <ProtectedRoute>
              <Layout />
            </ProtectedRoute>
          }
        >
          <Route index element={<Dashboard />} />
          <Route path="dashboard" element={<Dashboard />} />
          <Route path="games" element={<Games />} />
          <Route path="games/:gameId" element={<GameDetail />} />
          <Route path="wishlist" element={<WishList />} />
          <Route path="achievements" element={<AchievementsBrowse />} />
          <Route path="priorities" element={<Priorities />} />
          <Route path="multiplayer" element={<Multiplayer />} />
          <Route path="notifications" element={<Notifications />} />
          <Route path="statistics" element={<Statistics />} />
          <Route path="social" element={<Social />} />
          <Route path="browse" element={<Browse />} />
          <Route path="browse/version/:groupId" element={<VersionHub />} />
          <Route path="browse/:gameId" element={<BrowseGameInfo />} />
          <Route path="import-export" element={<AdminRoute><ImportExport /></AdminRoute>} />
          <Route path="settings" element={<Settings />} />
          <Route path="signatures" element={<SignatureGenerator />} />
          <Route path="account" element={<AccountSettings />} />
          <Route path="library-maintenance" element={<LibraryMaintenance />} />
          <Route path="master-database" element={<AdminRoute><MasterDatabase /></AdminRoute>} />
          <Route path="master-database/config" element={<AdminRoute><MasterDatabaseConfig /></AdminRoute>} />
          <Route path="master-database/community" element={<AdminRoute><MasterDatabaseCommunity /></AdminRoute>} />
          <Route path="master-database/links" element={<AdminRoute><MasterDatabaseLinks /></AdminRoute>} />
          <Route path="master-database/tags" element={<AdminRoute><MasterDatabaseTags /></AdminRoute>} />
          <Route path="master-database/milestones" element={<AdminRoute><MasterDatabaseMilestones /></AdminRoute>} />
          <Route path="master-database/images" element={<AdminRoute><MasterDatabaseImages /></AdminRoute>} />
          <Route path="links" element={<Links />} />
          <Route path="tutorials" element={<Tutorials />} />
          <Route path="tutorials/admin" element={<AdminRoute><TutorialsAdmin /></AdminRoute>} />
          <Route path="tutorials/:id" element={<TutorialDetail />} />
          <Route path="upgrade" element={<Upgrade />} />
          <Route path="super-admin" element={<SuperAdminDashboard />} />
        </Route>
        
        {/* Legacy routes - redirect to /app/* for backward compatibility */}
        <Route
          path="/dashboard"
          element={
            <ProtectedRoute>
              <Layout />
            </ProtectedRoute>
          }
        >
          <Route index element={<Dashboard />} />
        </Route>
        <Route
          path="/games/*"
          element={
            <ProtectedRoute>
              <Layout />
            </ProtectedRoute>
          }
        >
          <Route index element={<Games />} />
          <Route path=":gameId" element={<GameDetail />} />
        </Route>
        <Route
          path="/wishlist"
          element={
            <ProtectedRoute>
              <Layout />
            </ProtectedRoute>
          }
        >
          <Route index element={<WishList />} />
        </Route>
        <Route
          path="/achievements"
          element={
            <ProtectedRoute>
              <Layout />
            </ProtectedRoute>
          }
        >
          <Route index element={<AchievementsBrowse />} />
        </Route>
        <Route
          path="/priorities"
          element={
            <ProtectedRoute>
              <Layout />
            </ProtectedRoute>
          }
        >
          <Route index element={<Priorities />} />
        </Route>
        <Route
          path="/multiplayer"
          element={
            <ProtectedRoute>
              <Layout />
            </ProtectedRoute>
          }
        >
          <Route index element={<Multiplayer />} />
        </Route>
        <Route
          path="/notifications"
          element={
            <ProtectedRoute>
              <Layout />
            </ProtectedRoute>
          }
        >
          <Route index element={<Notifications />} />
        </Route>
        <Route
          path="/statistics"
          element={
            <ProtectedRoute>
              <Layout />
            </ProtectedRoute>
          }
        >
          <Route index element={<Statistics />} />
        </Route>
        <Route
          path="/social"
          element={
            <ProtectedRoute>
              <Layout />
            </ProtectedRoute>
          }
        >
          <Route index element={<Social />} />
        </Route>
        <Route
          path="/browse/*"
          element={
            <ProtectedRoute>
              <Layout />
            </ProtectedRoute>
          }
        >
          <Route index element={<Browse />} />
          <Route path="version/:groupId" element={<VersionHub />} />
          <Route path=":gameId" element={<BrowseGameInfo />} />
        </Route>
        <Route
          path="/import-export"
          element={
            <ProtectedRoute>
              <Layout />
            </ProtectedRoute>
          }
        >
          <Route index element={<AdminRoute><ImportExport /></AdminRoute>} />
        </Route>
        <Route
          path="/settings"
          element={
            <ProtectedRoute>
              <Layout />
            </ProtectedRoute>
          }
        >
          <Route index element={<Settings />} />
        </Route>
        <Route
          path="/signatures"
          element={
            <ProtectedRoute>
              <Layout />
            </ProtectedRoute>
          }
        >
          <Route index element={<SignatureGenerator />} />
        </Route>
        <Route
          path="/account"
          element={
            <ProtectedRoute>
              <Layout />
            </ProtectedRoute>
          }
        >
          <Route index element={<AccountSettings />} />
        </Route>
        <Route
          path="/library-maintenance"
          element={
            <ProtectedRoute>
              <Layout />
            </ProtectedRoute>
          }
        >
          <Route index element={<LibraryMaintenance />} />
        </Route>
        <Route
          path="/master-database/*"
          element={
            <ProtectedRoute>
              <AdminRoute><Layout /></AdminRoute>
            </ProtectedRoute>
          }
        >
          <Route index element={<MasterDatabase />} />
          <Route path="config" element={<MasterDatabaseConfig />} />
          <Route path="community" element={<MasterDatabaseCommunity />} />
          <Route path="links" element={<MasterDatabaseLinks />} />
          <Route path="tags" element={<MasterDatabaseTags />} />
          <Route path="milestones" element={<MasterDatabaseMilestones />} />
          <Route path="images" element={<MasterDatabaseImages />} />
        </Route>
        <Route
          path="/super-admin"
          element={
            <ProtectedRoute>
              <Layout />
            </ProtectedRoute>
          }
        >
          <Route index element={<SuperAdminDashboard />} />
          <Route path="parental-reports" element={<SuperAdminModeration />} />
        </Route>
        <Route
          path="/links"
          element={
            <ProtectedRoute>
              <Layout />
            </ProtectedRoute>
          }
        >
          <Route index element={<Links />} />
        </Route>
        <Route
          path="/tutorials"
          element={
            <ProtectedRoute>
              <Layout />
            </ProtectedRoute>
          }
        >
          <Route index element={<Tutorials />} />
          <Route path="admin" element={<AdminRoute><TutorialsAdmin /></AdminRoute>} />
          <Route path=":id" element={<TutorialDetail />} />
        </Route>
      </Routes>
    </AuthProvider>
  );
}

import { ErrorBoundary } from "@/components/ErrorBoundary";

function App() {
  return (
    <div className="App min-h-screen bg-[#050505]">
      <ErrorBoundary>
        <BrowserRouter>
          <AppRouter />
        </BrowserRouter>
        <Toaster position="bottom-right" theme="dark" />
      </ErrorBoundary>
    </div>
  );
}

export default App;
